import React, { useEffect, useState } from "react";
import { Card, Row, Col, Select, Typography, Spin, Empty } from "antd";
import Chart from "react-apexcharts";
import useRequest from "../hooks/useRequest";

const { Title, Text } = Typography;

function UserJourney() {
  const { request } = useRequest();

  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const hasReports = reports.length > 0;

  /* ---------------- FETCH PATIENTS ---------------- */
  useEffect(() => {
    request({
      url: "/api/admin/users?user_type=USER",
      method: "GET",
      onSuccess: (res) => setPatients(res?.data || []),
    });
  }, []);

  /* ---------------- FETCH REPORTS ---------------- */
  const fetchReportsByPatient = (patientId) => {
    setLoading(true);
    request({
      url: `/api/admin/reports?patient_id=${patientId}`,
      method: "GET",
      onSuccess: (res) => {
        setReports(res?.data || []);
        setLoading(false);
      },
      onError: () => setLoading(false),
    });
  };

  const generateColors = (count) => {
    const colors = [];

    for (let i = 0; i < count; i++) {
      const r = Math.floor(128 + 127 * Math.sin(0.8 * i));
      const g = Math.floor(128 + 127 * Math.sin(0.8 * i + 2));
      const b = Math.floor(128 + 127 * Math.sin(0.8 * i + 4));

      colors.push(`rgb(${r}, ${g}, ${b})`);
    }

    return colors;
  };

  /* ---------------- DERIVED DATA ---------------- */

  // LINE CHART (Visits timeline per report)
  const lineSeries = reports.map((report) => ({
    name: `Report #${report.id}`,
    data: (report.visits || []).map((v, index) => ({
      x: new Date(v.visit_date).getTime(), // 🔥 TIMESTAMP
      y: index + 1,                        // visit progression
    })),
  }));

  const lineColors = generateColors(reports.length);

  const lineOptions = {
    chart: {
      type: "line",
      zoom: { enabled: false },
      toolbar: { show: true },
    },

    colors: lineColors,

    stroke: {
      curve: "smooth",
      width: 3,
      dashArray: reports.map(r =>
        r.meta?.next_visit === null ? 6 : 0
      ),
    },

    markers: {
      size: 6,              // 🎯 important for selection
      hover: { size: 9 },
      strokeWidth: 2,
    },

    xaxis: {
      type: "datetime",
      title: { text: "Visit Date" },
    },

    yaxis: {
      title: { text: "Visit Progress" },
      min: 0,
      tickAmount: 5,
    },

    tooltip: {
      shared: false,        // ❌ disable shared
      intersect: true,      // ✅ select exact line
      x: {
        format: "dd MMM yyyy",
      },
    },

    legend: {
      position: "top",
      onItemHover: {
        highlightDataSeries: true, // improves UX
      },
    },

    grid: {
      strokeDashArray: 4,
    },
  };

  // BAR CHART (Visits count per report)
  const barSeries = [
    {
      name: "Visits",
      data: reports.map((r) => r.visits?.length || 0),
    },
  ];

  const barOptions = {
    chart: { type: "bar" },
    // colors: lineColors,
    xaxis: {
      categories: reports.map((r) => `Report #${r.id}`),
    },
  };

  // PIE CHART (Treatment status)
  const completed = reports.filter(
    (r) => r.meta && r.meta.next_visit === null
  ).length;

  const ongoing = reports.length - completed;

  const pieSeries = [completed, ongoing];

  const pieOptions = {
    labels: ["Completed", "Ongoing"],
    colors: ["#4CAF50", "#FF9800"],
    legend: { position: "bottom" },
  };

  /* ---------------- UI ---------------- */

  return (
    <div style={{ padding: 24 }}>
      <Title level={2}>Patient Treatment Journey</Title>

      {/* PATIENT SELECT */}
      <Card style={{ marginBottom: 24 }}>
        <Text strong>Select Patient</Text>
        <Select
          showSearch
          allowClear
          style={{ width: 320, marginLeft: 16 }}
          placeholder="Search patient"
          optionFilterProp="label"
          onChange={(value) => {
            setSelectedPatient(value);
            fetchReportsByPatient(value);
          }}
          options={patients.map((p) => ({
            value: p.id,
            label: `${p.full_name} (${p.email})`,
          }))}
        />
      </Card>

      {loading && <Spin size="large" />}

      {!selectedPatient && !loading && (
        <>
          {/* PLACEHOLDER SUMMARY CARDS */}
          <Row gutter={16} style={{ marginBottom: 24 }}>
            {["Total Reports", "Completed", "Ongoing", "Total Visits"].map((label) => (
              <Col span={6} key={label}>
                <Card>
                  <Title level={4} style={{ opacity: 0.5 }}>
                    {label}
                  </Title>
                  <Title level={2} style={{ opacity: 0.3 }}>
                    —
                  </Title>
                </Card>
              </Col>
            ))}
          </Row>

          {/* GUIDANCE MESSAGE */}
          <Card
            style={{
              textAlign: "center",
              padding: "80px 24px",
              marginBottom: 24,
            }}
          >
            <Title level={3}>Patient Treatment Journey</Title>
            <Text type="secondary" style={{ fontSize: 16 }}>
              Select a patient from the dropdown above to visualize their
              treatment timeline, visit frequency, and recovery progress.
            </Text>
          </Card>

          {/* EMPTY CHART PLACEHOLDERS */}
          <Card title="Visit Timeline (Per Report)" style={{ marginBottom: 24 }}>
            <div
              style={{
                height: 350,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#999",
              }}
            >
              Visit timeline will appear here
            </div>
          </Card>

          <Row gutter={24}>
            <Col span={12}>
              <Card title="Visits Per Report">
                <div
                  style={{
                    height: 300,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#999",
                  }}
                >
                  Visits comparison will appear here
                </div>
              </Card>
            </Col>

            <Col span={12}>
              <Card title="Treatment Status">
                <div
                  style={{
                    height: 300,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#999",
                  }}
                >
                  Treatment status overview will appear here
                </div>
              </Card>
            </Col>
          </Row>
        </>
      )}

      {!loading && selectedPatient && reports.length === 0 && (
        <Empty description="No reports found for this patient" />
      )}

      {!loading && selectedPatient && (
        <>
          {/* SUMMARY CARDS */}
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={6}>
              <Card>
                <Title level={4}>Total Reports</Title>
                <Title level={2}>{reports.length}</Title>
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Title level={4}>Completed</Title>
                <Title level={2}>{completed}</Title>
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Title level={4}>Ongoing</Title>
                <Title level={2}>{ongoing}</Title>
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Title level={4}>Total Visits</Title>
                <Title level={2}>
                  {reports.reduce((sum, r) => sum + (r.visits?.length || 0), 0)}
                </Title>
              </Card>
            </Col>
          </Row>

          {/* LINE CHART */}
          <Card title="Visit Timeline (Per Report)" style={{ marginBottom: 24 }}>
            {hasReports ? (
              <Chart
                options={lineOptions}
                series={lineSeries}
                type="line"
                height={350}
              />
            ) : (
              <Empty description="No visit timeline available" />
            )}
          </Card>

          <Row gutter={24}>
            {/* BAR CHART */}
            <Col span={12}>
              <Card title="Visits Per Report">
                {hasReports ? (
                  <Chart
                    options={barOptions}
                    series={barSeries}
                    type="bar"
                    height={300}
                  />
                ) : (
                  <Empty description="No visit data available" />
                )}
              </Card>
            </Col>

            {/* PIE CHART */}
            <Col span={12}>
              <Card title="Treatment Status">
                {hasReports ? (
                  <Chart
                    options={pieOptions}
                    series={pieSeries}
                    type="pie"
                    height={300}
                  />
                ) : (
                  <Empty description="No treatment status data" />
                )}
              </Card>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
}

export default UserJourney;
